<template>
    <!-- width는 wrapper에 적용 -->
    <div class="relative" :class="width">
        <div :class="computedBoxClass">
            <input
                type="text"
                :placeholder="placeholder"
                :value="displayValue"
                :maxlength="maxlength"
                :readonly="readonly"
                :disabled="disabled"
                :class="computedInputClass"
                @focus="isFocused = true"
                @keydown="onNumberKeyDown"
                @input="onInput"
                @blur="onBlur"
            />
            <span
                v-if="lastInnerString !== null"
                class="shrink-0 pr-3 pl-1 text-content-basic-3 pointer-events-none"
            >{{ lastInnerString }}</span>
        </div>
        <p v-if="error" class="mt-1 font-caption-md-regular text-accent-red-3">
            {{ error }}
        </p>
    </div>
</template>

<script>
export default {
    name: 'DesignAmountInput',
    props: {
        modelValue: { type: [String, Number], default: '' },
        placeholder: { type: String, default: '' },
        size: {
            type: String,
            default: 'LG',
            validator: (v) => ['MD', 'LG'].includes(String(v).toUpperCase()),
        },
        width: { type: String, default: 'w-full' },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        maxlength: Number,
        error: { type: String, default: '' },
        intPartLimit : {type: Number, default: null},
        lastInnerString: { type: String, default: null },
    },
    emits: ['update:modelValue', 'blur'],
    data() {
        return {
            boxBaseClass: [
                'flex items-center overflow-hidden',
                'bg-background-basic-1 rounded-02',
                'border border-border-basic-2',
                'focus-within:border-primary-3',
                'transition-colors',
            ],
            boxClassMap: {
                LG: 'h-[2.75rem]',
                MD: 'h-[2.25rem]',
            },
            inputBaseClass: [
                'w-full border-none bg-[rgba(0,0,0,0)]',
                'focus:outline-none focus:ring-0',
                'placeholder:text-content-basic-3',
            ],
            inputTextMap: {
                LG: 'font-body-sm-regular',
                MD: 'font-body-sm-regular',
            },
            displayValue: '',
            isFocused: false,
        };
    },
    computed: {
        normalizedSize() {
            return String(this.size).toUpperCase();
        },
        computedBoxClass() {
            const size = this.normalizedSize;
            const sizeClass = this.boxClassMap[size] || '';
            const classes = [...this.boxBaseClass, sizeClass];
            if (this.disabled) {
                classes.push('bg-background-basic-3');
            }
            if (this.readonly) {
                classes.push('bg-background-basic-3');
            }
            if (this.error) {
                classes.push('border-accent-red-3', 'focus-within:border-accent-red-3');
            }
            return classes.join(' ');
        },
        computedInputClass() {
            const size = this.normalizedSize;
            const textClass = this.inputTextMap[size] || '';
            const bgClass = (this.disabled || this.readonly)
                ? 'bg-background-basic-3'
                : 'bg-background-basic-1';
            return [...this.inputBaseClass, textClass, bgClass].join(' ');
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(val) {
                if (!this.isFocused) {
                    this.displayValue = this.formatAmount(val);
                }
            },
        },
    },
    methods: {
        onNumberKeyDown(event) {
            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
            const val = event.target.value;

            if (event.key === '.' && val.length > 0 && !val.includes('.')) {
                return true;
            }
            if (event.key >= '0' && event.key <= '9') {
                return true;
            }
            if (allowedKeys.includes(event.key)) {
                return true;
            }

            event.preventDefault();
            return false;
        },
        onInput(event) {
            const { formatted, raw } = this.normalizeAmount(event.target.value);
            this.displayValue = formatted;
            event.target.value = formatted;
            this.$emit('update:modelValue', raw === '' ? null : Number.parseFloat(raw));
        },
        onBlur(event) {
            this.isFocused = false;
            this.displayValue = this.formatAmount(this.modelValue);
            this.$emit('blur', event);
        },
        formatAmount(value) {
            if (value == null || value === '') {
                return '';
            }

            return this.normalizeAmount(String(value)).formatted;
        },
        normalizeAmount(value) {
            let rawValue = String(value ?? '').replace(/[^0-9.]/g, '');
            const parts = rawValue.split('.');

            if (parts.length > 2) {
                rawValue = `${parts[0]}.${parts[1]}`;
            }

            const hasDecimalPoint = rawValue.includes('.');
            let [intPart, decimalPart = ''] = rawValue.split('.');

            decimalPart = decimalPart.slice(0, 2);
            intPart = intPart.replace(/^0+(?=\d)/, '');
            if(this.intPartLimit){
                intPart = intPart.slice(0, this.intPartLimit);
            }

            if (!intPart && (decimalPart || hasDecimalPoint)) {
                intPart = '0';
            }

            if (!intPart && !decimalPart && !hasDecimalPoint) {
                return { formatted: '', raw: '' };
            }

            const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            const formatted = hasDecimalPoint
                ? `${formattedInt}.${decimalPart}`
                : formattedInt;
            const raw = hasDecimalPoint
                ? `${intPart}.${decimalPart}`
                : intPart;

            return { formatted, raw };
        },
    },
};
</script>
